import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAgents, deleteAgent } from '../../actions/agent';
import { Dropdown } from 'react-bootstrap';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    Eye,
    Phone,
    MapPin,
    Clock,
    User,
    Filter,
    Download,
    Briefcase
} from 'lucide-react';

const Agents = ({ getAgents, deleteAgent, agents, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [boundaryFilter, setBoundaryFilter] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        getAgents();
    }, [getAgents]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleDeleteClick = (agent) => {
        setAgentToDelete(agent);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (agentToDelete) {
            try {
                setIsDeleting(true);
                await deleteAgent(agentToDelete._id);
                setShowDeleteModal(false);
                setAgentToDelete(null);
            } catch (error) {
                console.error('Error deleting agent:', error);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setAgentToDelete(null);
    };

    const getStatusBadgeClass = (status) => {
        const statusClasses = {
            online: 'bg-success',
            offline: 'bg-secondary',
            busy: 'bg-warning',
            on_delivery: 'bg-info',
            on_refill: 'bg-primary',
            low_inventory: 'bg-danger'
        };
        return statusClasses[status] || 'bg-secondary';
    };

    const getStatusDisplayText = (status) => {
        const statusText = {
            online: 'Online',
            offline: 'Offline',
            busy: 'Busy',
            on_delivery: 'On Delivery',
            on_refill: 'On Refill',
            low_inventory: 'Low Inventory'
        };
        return statusText[status] || status;
    };

    const filteredAgents = agents?.filter(agent => {
        const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            agent.phone.includes(searchTerm) ||
                            agent.drivingLicenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = !statusFilter || agent.status === statusFilter;
        const matchesBoundary = !boundaryFilter || agent.boundaryType === boundaryFilter;
        
        return matchesSearch && matchesStatus && matchesBoundary;
    }) || [];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading agents...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary mb-1 fw-bold">
                                    <Briefcase size={24} className="me-2" />
                                    Delivery Agents
                                </h2>
                                <p className="text-muted mb-0">
                                    Manage delivery agents and their information
                                </p>
                            </div>
                            <Link 
                                to="/inventory/v1.2/agents/new" 
                                className="btn btn-primary d-flex align-items-center"
                            >
                                <Plus size={16} className="me-2" />
                                Add New Agent
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">{agents?.length || 0}</h5>
                                        <p className="card-text small mb-0">Total Agents</p>
                                    </div>
                                    <User size={24} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">
                                            {agents?.filter(a => a.status === 'online').length || 0}
                                        </h5>
                                        <p className="card-text small mb-0">Online</p>
                                    </div>
                                    <div className="rounded-circle bg-light" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <div className="bg-success rounded-circle" style={{ width: '12px', height: '12px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">
                                            {agents?.filter(a => a.status === 'on_delivery').length || 0}
                                        </h5>
                                        <p className="card-text small mb-0">On Delivery</p>
                                    </div>
                                    <Briefcase size={24} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">
                                            {agents?.filter(a => a.isApproved).length || 0}
                                        </h5>
                                        <p className="card-text small mb-0">Approved</p>
                                    </div>
                                    <div className="rounded-circle bg-light" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="fas fa-check text-warning"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={16} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, phone, or license..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="online">Online</option>
                                    <option value="offline">Offline</option>
                                    <option value="busy">Busy</option>
                                    <option value="on_delivery">On Delivery</option>
                                    <option value="on_refill">On Refill</option>
                                    <option value="low_inventory">Low Inventory</option>
                                </select>
                            </div>
                            <div className="col-md-3 mb-3">
                                <select
                                    className="form-select"
                                    value={boundaryFilter}
                                    onChange={(e) => setBoundaryFilter(e.target.value)}
                                >
                                    <option value="">All Boundary Types</option>
                                    <option value="fixed">Fixed</option>
                                    <option value="floating">Floating</option>
                                </select>
                            </div>
                            <div className="col-md-2 mb-3">
                                <button className="btn btn-outline-primary w-100">
                                    <Download size={16} className="me-2" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Agents Grid */}
                <div className="row">
                    {filteredAgents.length > 0 ? (
                        filteredAgents.map((agent) => (
                            <div key={agent._id} className="col-lg-4 col-md-6 mb-4">
                                <div className="card shadow-sm h-100 agent-card">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="agent-avatar me-3">
                                                    {agent.documents?.agentPhoto ? (
                                                        <img 
                                                            src={`${process.env.REACT_APP_SERVER_PUBLIC_PATH}/${agent.documents.agentPhoto}`} 
                                                            alt={agent.name}
                                                            className="rounded-circle"
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white" style={{ width: '50px', height: '50px' }}>
                                                            {agent.name?.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <h6 className="card-title mb-1">{agent.name}</h6>
                                                    <div className="d-flex align-items-center">
                                                        <span className={`badge ${getStatusBadgeClass(agent.status)} me-2`}>
                                                            {getStatusDisplayText(agent.status)}
                                                        </span>
                                                        {agent.isApproved && (
                                                            <span className="badge bg-success">
                                                                <i className="fas fa-check me-1"></i>
                                                                Approved
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                    Actions
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item as={Link} to={`/inventory/v1.2/agents/${agent._id}`}>
                                                        <Eye size={14} className="me-2" />
                                                        View Details
                                                    </Dropdown.Item>                                                    
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item onClick={() => handleDeleteClick(agent)} className="text-danger">
                                                        <Trash2 size={14} className="me-2" />
                                                        Delete
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>

                                        <div className="row text-center mb-3">
                                            <div className="col-4">
                                                <div className="border-end">
                                                    <div className="fw-bold text-primary">
                                                        {agent.boundaryType}
                                                    </div>
                                                    <small className="text-muted">Boundary</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="border-end">
                                                    <div className="fw-bold text-success">
                                                        {agent.isActive ? 'Active' : 'Inactive'}
                                                    </div>
                                                    <small className="text-muted">Status</small>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div className="fw-bold text-info">
                                                    {new Date(agent.joiningDate).toLocaleDateString()}
                                                </div>
                                                <small className="text-muted">Joined</small>
                                            </div>
                                        </div>

                                        <div className="agent-details">
                                            <div className="d-flex align-items-center mb-2">
                                                <Phone size={14} className="me-2 text-muted" />
                                                <span className="small">{agent.phone}</span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <MapPin size={14} className="me-2 text-muted" />
                                                <span className="small text-truncate">
                                                    {agent.permanentAddress}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="fas fa-id-card me-2 text-muted" style={{ fontSize: '14px' }}></i>
                                                <span className="small">{agent.drivingLicenseNumber}</span>
                                            </div>
                                            {agent.currentLocation?.lastUpdated && (
                                                <div className="d-flex align-items-center">
                                                    <Clock size={14} className="me-2 text-muted" />
                                                    <span className="small">
                                                        Last seen: {new Date(agent.currentLocation.lastUpdated).toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12">
                            <div className="text-center py-5">
                                <Briefcase size={64} className="text-muted mb-3" />
                                <h5 className="text-muted mb-3">
                                    {searchTerm || statusFilter || boundaryFilter ? 'No agents found' : 'No agents yet'}
                                </h5>
                                <p className="text-muted mb-4">
                                    {searchTerm || statusFilter || boundaryFilter 
                                        ? 'Try adjusting your search criteria or filters.' 
                                        : 'Get started by adding your first delivery agent.'
                                    }
                                </p>
                                {!searchTerm && !statusFilter && !boundaryFilter && (
                                    <Link 
                                        to="/inventory/v1.2/agents/new" 
                                        className="btn btn-primary"
                                    >
                                        <Plus size={16} className="me-2" />
                                        Add First Agent
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={handleDeleteCancel}
                                    disabled={isDeleting}
                                ></button>
                            </div>
                            <div className="modal-body text-center py-4">
                                <div className="mb-3">
                                    <Trash2 size={48} className="text-danger mb-3" />
                                </div>
                                <h5 className="mb-3">Delete Agent "{agentToDelete?.name}"?</h5>
                                <p className="text-muted mb-0">
                                    This action cannot be undone. The agent and all associated data will be permanently removed.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleDeleteCancel}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={handleDeleteConfirm}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} className="me-2" />
                                            Delete Agent
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom CSS */}
            <style>{`
                .agent-card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    border: 1px solid #e3e6f0;
                }

                .agent-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }

                .agent-avatar img,
                .agent-avatar div {
                    border: 2px solid #fff;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .border-end:last-child {
                    border-right: none !important;
                }

                .agent-details .small {
                    font-size: 0.875rem;
                }

                .text-truncate {
                    max-width: 200px;
                }

                @media (max-width: 768px) {
                    .agent-card .card-body {
                        padding: 1rem;
                    }
                    
                    .agent-details .small {
                        font-size: 0.8rem;
                    }
                }
            `}</style>
        </Fragment>
    );
};

Agents.propTypes = {
    getAgents: PropTypes.func.isRequired,
    deleteAgent: PropTypes.func.isRequired,
    agents: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    agents: state.agent.agents,
    loading: state.agent.loading
});

export default connect(mapStateToProps, { getAgents, deleteAgent })(Agents);
